'use client';

import { useState, useEffect } from 'react';
import { IRepository, ICommit } from '@/types';
import Link from 'next/link';
import { IEvaluation } from '@/lib/evaluationsApi';
import pluralize from 'pluralize';
import { secondsToTime } from '@/lib/dateTime';
import { getEvaluationTarget } from '../lib';
import { getApproximateCost, getPercentComplete } from '../lib';
import Tag, { TagProps, TagVariant } from '@/ui/Tag';
import * as Icon from '@/ui/Icon';
import {
  LayersIcon,
  ArrowRightIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ColorWheelIcon,
  RowsIcon,
  ClockIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';

const runTime = (evaluation: IEvaluation) => {
  if (!evaluation.completed_at) return undefined;
  const startedAt = new Date(evaluation.started_at!);
  const completedAt = new Date(evaluation.completed_at!);
  const seconds = Math.floor((completedAt.getTime() - startedAt.getTime()) / 1000);
  return secondsToTime(seconds);
};

const runningTime = (evaluation: IEvaluation) => {
  if (!evaluation.started_at) return undefined;
  const startedAt = new Date(evaluation.started_at!);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
  return secondsToTime(seconds);
};

const TargetLinkWrapper: React.FC<{ repository: IRepository; evaluation: IEvaluation; children: React.ReactNode }> = ({
  repository,
  evaluation,
  children,
}) => {
  const target = getEvaluationTarget(repository, evaluation);
  return target ? (
    <Link href={target.url} target="_blank" className="link-underline">
      {children}
    </Link>
  ) : (
    children
  );
};

const WorkSpaceTag: React.FC<{ repository: IRepository; evaluation: IEvaluation }> = ({
  repository,
  evaluation,
  ...rest
}) => {
  const tagVariant = !!evaluation.workspace?.closing_commit ? 'error' : 'primary';
  const shortWorkspaceId = evaluation.workspace?.id?.split('-').slice(-1)[0];
  const isClosed = !!evaluation.workspace?.closing_commit;
  if (isClosed) {
    return (
      <TargetLinkWrapper repository={repository} evaluation={evaluation}>
        <CommitTag repository={repository} commit={evaluation.workspace?.closing_commit!} {...rest}>
          <LayersIcon height={12} />
          <ArrowRightIcon height={12} />
        </CommitTag>
      </TargetLinkWrapper>
    );
  }
  return (
    <>
      <TargetLinkWrapper repository={repository} evaluation={evaluation}>
        <Tag small variant={tagVariant} {...rest}>
          <LayersIcon height={12} />
          {isClosed ? 'Closed' : shortWorkspaceId}
        </Tag>
      </TargetLinkWrapper>
      {isClosed && <CommitTag repository={repository} commit={evaluation.workspace?.closing_commit!} {...rest} />}
    </>
  );
};

const CommitTag: React.FC<{ repository: IRepository; commit: ICommit; children?: React.ReactNode }> = ({
  repository,
  commit,
  children,
  ...rest
}) => {
  return (
    commit && (
      <Tag small variant="info" style={{ gap: 0, maxWidth: '100%' }} {...rest}>
        {children}
        <Icon.Commit height={12} />
        <span className="truncate">{commit.id.split('-').slice(-1)[0]}</span>
      </Tag>
    )
  );
};

const EvaluationTargetTag: React.FC<TagProps & { repository: IRepository; evaluation: IEvaluation }> = ({
  repository,
  evaluation,
  ...rest
}) => {
  const isSample = evaluation.is_sample;
  const isCancelled = evaluation.status === 'cancelled';
  const isErrored = evaluation.status === 'error';
  const isDead = isCancelled || isErrored;

  if (isSample) {
    return <Tag small>Sample - N/A</Tag>;
  }

  if (isDead) {
    return <Tag small>N/A</Tag>;
  }

  if (evaluation.closing_commit) {
    return <CommitTag repository={repository} commit={evaluation.closing_commit} {...rest} />;
  }
  return <WorkSpaceTag repository={repository} evaluation={evaluation} {...rest} />;
};

const IterationsLinkWrapper: React.FC<{
  repository?: IRepository;
  evaluation: IEvaluation;
  children: React.ReactNode;
}> = ({ repository, evaluation, children }) =>
  repository ? (
    <Link
      href={`/${repository.namespace}/${repository.name}/evaluations/${evaluation.id}/history`}
      // className="link-underline"
    >
      {children}
    </Link>
  ) : (
    <>{children}</>
  );

const IterationsTag: React.FC<
  TagProps & { evaluation: IEvaluation; repository?: IRepository; variant?: TagVariant }
> = ({ evaluation, repository, variant, ...rest }) => {
  if (!evaluation.iterations_count) return null;

  return (
    <Tag variant="info" small {...rest}>
      <ReloadIcon />{' '}
      <IterationsLinkWrapper repository={repository} evaluation={evaluation}>
        {pluralize('iteration', evaluation.iterations_count, true)}
      </IterationsLinkWrapper>
    </Tag>
  );
};

const DateTimeHydrator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [value, setValue] = useState<React.ReactNode | null>(null);
  useEffect(() => {
    setValue(children);
  }, [children]);
  return <>{value}</>;
};

// const RunTimeTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
//   evaluation,
//   variant,
//   ...rest
// }) => {
//   const currentRunTime = runTime(evaluation);
//   return currentRunTime ? (
//     <Tag variant={variant || 'success'} small {...rest}>
//       <ClockIcon /> <DateTimeHydrator>{runTime(evaluation)}</DateTimeHydrator>
//     </Tag>
//   ) : evaluation.status === 'started' ? (
//     <Tag variant={variant || 'primary'} small {...rest}>
//       <ClockIcon /> Running <DateTimeHydrator>{runningTime(evaluation)}</DateTimeHydrator>
//     </Tag>
//   ) : (
//     <Tag
//       variant={variant || evaluation.status === 'error' ? 'error' : 'default'}
//       small
//       {...rest}
//       style={{ maxWidth: '100%' }}
//     >
//       <ClockIcon height={16} style={{ minWidth: 15 }} />{' '}
//       <span className="text-line-truncate">
//         {evaluation.status === 'error' ? evaluation.error_message : evaluation.status}
//       </span>
//     </Tag>
//   );
// };

const RunTimeTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  const currentRunTime = runTime(evaluation);
  const isStarted = evaluation.status === 'started';
  const isError = evaluation.status === 'error';

  const getTagVariant = () => {
    if (variant) return variant;
    if (currentRunTime) return 'success';
    if (isStarted) return 'primary';
    if (isError) return 'error';
    return 'default';
  };

  const getTagContent = () => {
    if (currentRunTime) {
      return <DateTimeHydrator>{currentRunTime}</DateTimeHydrator>;
    }
    if (isStarted) {
      return (
        <>
          Running <DateTimeHydrator>{runningTime(evaluation)}</DateTimeHydrator>
        </>
      );
    }
    return <span className="text-line-truncate">{isError ? evaluation.error_message : evaluation.status}</span>;
  };

  return (
    <Tag variant={getTagVariant()} small {...rest}>
      <ClockIcon height={16} style={{ minWidth: 15 }} />
      {!currentRunTime && !isStarted ? ' ' : null}
      {getTagContent()}
    </Tag>
  );
};

const RowsProcessedTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  const noProgress = !evaluation.progress?.total;
  // if (!evaluation.progress?.total) return null;
  const isCompleted = evaluation.progress.processed === evaluation.progress.total;

  const getCountContent = () => {
    if (noProgress) return 'Waiting...';
    if (isCompleted) return evaluation.progress.processed;
    return `${evaluation.progress.processed} / ${evaluation.progress.total}`;
  };

  const getSuffix = () => {
    if (noProgress) return '';
    if (evaluation.is_sample) return 'row sample';
    return 'rows';
  };

  return (
    <Tag variant={variant || (isCompleted ? 'success' : 'info')} small {...rest}>
      <RowsIcon /> {getCountContent()} {getSuffix()}
    </Tag>
  );
};

const TokensUsedTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  const tokensUsed = evaluation.tokens_used || evaluation.prompt_tokens_used || evaluation.completion_tokens_used;
  // if (!tokensUsed) return null;
  const isCompleted = evaluation.progress.processed === evaluation.progress.total;
  return (
    <Tag variant={variant || (isCompleted ? 'success' : 'info')} small {...rest}>
      <ColorWheelIcon />
      {tokensUsed || 0} tokens
    </Tag>
  );
};

const CostTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  if (!evaluation.credits_used) return null;
  const isCompleted = evaluation.progress.processed === evaluation.progress.total;
  return (
    <Tag variant={variant || (isCompleted ? 'success' : 'info')} small {...rest}>
      <span>$</span> {getApproximateCost(evaluation)}
    </Tag>
  );
};

const TypeTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  return (
    <Tag small variant={variant || 'default'} {...rest}>
      {evaluation.input_type} → {evaluation.output_type}
    </Tag>
  );
};

const ProviderTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  const provider = evaluation.model?.provider;
  const providerName = provider.display_name || provider.name;
  return (
    <Tag small variant={variant || 'default'} {...rest}>
      {provider.logo && (
        <img
          src={provider.logo}
          alt={providerName}
          title={providerName}
          width={16}
          height={16}
          style={{ verticalAlign: 'middle' }}
        />
      )}
      <span> {providerName}</span>
    </Tag>
  );
};

const DeveloperTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  const developer = evaluation.model?.developer;
  const developerName = developer?.display_name || developer?.name;
  return (
    <Tag small variant={variant || 'default'} {...rest}>
      {developer?.logo && (
        <img
          src={developer?.logo}
          alt={developerName}
          title={developerName}
          width={16}
          height={16}
          style={{ verticalAlign: 'middle' }}
        />
      )}
      <span> {developerName}</span>
    </Tag>
  );
};

const ModelTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  ...rest
}) => {
  const model = evaluation.model;
  return (
    <Tag small variant={variant || 'default'} {...rest}>
      {model.provider.logo && (
        <img
          src={model.provider.logo}
          alt={model.provider.name}
          title={model.provider.name}
          width={16}
          height={16}
          style={{ verticalAlign: 'middle' }}
        />
      )}
      <span> {model.display_name || model.name}</span>
    </Tag>
  );
};

const ModelProviderTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant }> = ({
  evaluation,
  variant,
  truncate = true,
  ...rest
}) => {
  const model = evaluation.model;
  const provider = model.provider;
  const providerName = provider.display_name || provider.name;
  const modelName = model.display_name || model.name;

  return (
    <Tag small variant={variant || 'default'} {...rest} truncate={truncate}>
      {model.provider.logo && (
        <img
          src={model.provider.logo}
          alt={model.provider.name}
          title={model.provider.name}
          width={16}
          height={16}
          style={{ verticalAlign: 'middle' }}
        />
      )}
      <span className="truncate">
        {providerName}/{modelName}
      </span>
    </Tag>
  );
};

const ModelDeveloperTag: React.FC<TagProps & { evaluation: IEvaluation; variant?: TagVariant; logoSize?: number }> = ({
  evaluation,
  variant,
  truncate = true,
  logoSize = 16,
  ...rest
}) => {
  const model = evaluation.model;
  const developer = model.developer;
  const developerName = developer?.display_name || developer?.name || 'Unknown';
  const modelName = model.display_name || model.name;

  return (
    <Tag small variant={variant || 'default'} {...rest} truncate={truncate}>
      {developer?.logo && (
        <img
          src={developer?.logo}
          alt={developerName}
          title={developerName}
          width={logoSize}
          height={logoSize}
          style={{ verticalAlign: 'middle' }}
        />
      )}
      <span className="truncate">
        {developerName}/{modelName}
      </span>
    </Tag>
  );
};

const StatusTag: React.FC<
  TagProps & { evaluation: IEvaluation; variant?: TagVariant; style?: React.CSSProperties }
> = ({ evaluation, variant, style, ...rest }) => {
  const percentComplete = getPercentComplete(evaluation);
  const statusVariant = (() => {
    switch (evaluation.status) {
      case 'error':
        return 'error';
      case 'completed':
        return 'success';
      case 'started':
        return 'primary';
      default:
        return 'default';
    }
  })();

  const humanPercentComplete =
    evaluation.status === 'started' && percentComplete
      ? Math.round((percentComplete + Number.EPSILON) * 100) / 100
      : undefined;

  const getStatusIcon = () => {
    if (evaluation.status === 'started') return <ReloadIcon className="animate-spin" />;
    if (evaluation.status === 'completed') return <CheckIcon />;
    if (evaluation.status === 'error') return <ExclamationTriangleIcon />;
    return null;
  };

  return (
    <Tag small variant={variant || statusVariant} style={{ textTransform: 'capitalize', ...style }} {...rest}>
      {getStatusIcon()} {evaluation.status}{' '}
      {evaluation.status === 'started' && humanPercentComplete && `(${humanPercentComplete}%)`}
    </Tag>
  );
};

export {
  IterationsTag as Iterations,
  RunTimeTag as RunTime,
  RowsProcessedTag as Rows,
  TokensUsedTag as Tokens,
  TypeTag as Type,
  ModelTag as Model,
  ProviderTag as Provider,
  DeveloperTag as Developer,
  ModelProviderTag as ModelProvider,
  ModelDeveloperTag as ModelDeveloper,
  CostTag as Cost,
  StatusTag as Status,
  EvaluationTargetTag as Target,
};
